import React, {type PropsWithChildren} from 'react';
import type {SuggestionProps} from '@tiptap/suggestion';

import {TildeIcon} from '@time-webkit/all/icons/tilde';
import {LockIcon} from '@time-webkit/all/icons/lock';
import classNames from 'classnames';
import {TypographySize, useTypography} from '@time-webkit/all/hooks/typography';

import {sendToStatist} from '@time-webkit/statist';
import {type IEditor} from 'packages/editor';

import type {ChannelMention} from '../../types';

import {MentionList} from '../../../../components/mention-list';

import styles from './styles.module.css';

type MentionListProps = {
    getMentions: (query: string) => Promise<ChannelMention[]>;
    editor?: IEditor;
} & Pick<SuggestionProps<ChannelMention>, 'query' | 'command' | 'decorationNode'>;

const Description: React.FC<PropsWithChildren & {className?: string}> = (props) => {
    const typographyCls = useTypography({
        size: TypographySize.BodyS,
        noDefaultColor: true,
    });

    return <span className={classNames(styles.description, typographyCls, props.className)}>{props.children}</span>;
};

const Handle: React.FC<PropsWithChildren & {className?: string}> = (props) => {
    const typographyCls = useTypography({
        size: TypographySize.BodyL,
    });

    return <span className={classNames(styles.handle, typographyCls, props.className)}>{props.children}</span>;
};

const ChannelOption = (props: ChannelMention) => {
    const isPrivateChannelOption = props.type === 'P';
    return (
        <div className={styles.mentionWrapper}>
            <span className={classNames(styles.icon, styles.groupIcon)}>
                {isPrivateChannelOption ? <LockIcon /> : <TildeIcon />}
            </span>
            <Handle className={styles.maxPriority}>{props.displayName}</Handle>
            <Description className={styles.minPriority}>{`~${props.handle}`}</Description>
        </div>
    );
};

export const ChannelMentionList: React.FC<MentionListProps> = (props) => {
    const {query, getMentions, command, decorationNode} = props;
    const onSelect = (_: ChannelMention, index: number) => {
        sendToStatist('editor.searchChannel.resultTap', {
            resultNumber: index + 1,
        });
    };

    return (
        <MentionList
            className={styles.channelMentionList}
            query={query}
            command={command}
            decorationNode={decorationNode}
            getMentions={getMentions}
            renderMentionOption={(item) => <ChannelOption {...item} />}
            onSelect={onSelect}
            ignoreEmptyQuery={false}
            editor={props.editor}
            shouldRequestOnFocus={true}
        />
    );
};
